import { useCurrentOrganization, useWorkspaceSettings } from '@easy-expense/data-firestore-client';
import { DateFormat, DistanceUnit, TaxRate } from '@easy-expense/data-schema-v2';
import { getTranslation } from '@easy-expense/intl-client';
import { CountryCode } from '@easy-expense/intl-shared';
import { Icon } from '@easy-expense/ui-shared-components';
import { SHADOW_STYLE } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { getBusinessTaxRate, PROVINCES } from '@easy-expense/utils-shared';
import { useState, useEffect, useMemo } from 'react';
import CountryPicker, { Country } from 'react-native-country-picker-modal';

import { Survey } from '../../components/OnboardingFlow/Survey/Survey';

const ProvinceSelector: React.FC<{
  onSelect: (options: string[]) => void;
  selectedOptions: string[];
}> = ({ onSelect, selectedOptions }) => {
  return (
    <Survey
      header={''}
      text={''}
      options={PROVINCES.map((province) => {
        return {
          title: province.name,
        };
      })}
      multiSelect={false}
      onOptionSelected={(options: string[]) => onSelect(options)}
      selectedOptions={selectedOptions}
    />
  );
};

export const DEFAULT_COUNTRY: Country = {
  callingCode: ['1'],
  cca2: 'US',
  currency: ['USD'],
  flag: 'flag-us',
  name: 'United States',
  region: 'Americas',
  subregion: 'North America',
};

export const CountrySelector = ({
  province,
  country,
  setCountry,
  setProvince,
}: {
  province?: string;
  country: Country;
  setProvince: (name: string) => void;
  setCountry: (code: Country) => void;
}) => {
  const { updateWorkspaceSetting } = useWorkspaceSettings();
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const organization = useCurrentOrganization();

  useEffect(() => {
    const countryCode = country.cca2 as CountryCode;
    const currencyCode = country.currency[0];
    const prov = PROVINCES.find((p) => p.name === province);
    let dateFormat: DateFormat = 'MM/dd/yy';
    let distanceUnit: DistanceUnit = 'Mile';
    const taxRates: TaxRate[] =
      organization?.organizationType === 'personal'
        ? []
        : getBusinessTaxRate(countryCode, prov?.abbr);

    if (country.name === 'United States') {
      dateFormat = 'MM/dd/yy';
    } else {
      dateFormat = 'dd/MM/yy';
    }

    if (country.name === 'United States' || country.name === 'United Kingdom') {
      distanceUnit = 'Mile';
    } else {
      distanceUnit = 'Kilometer';
    }

    updateWorkspaceSetting({
      countryCode,
      currencyCode,
      distanceUnit,
      dateFormat,
      taxRates,
      state: prov?.abbr,
    });
  }, [country, province]);

  useEffect(() => {
    setCountry(country);
  }, [country]);

  const isCanada = useMemo(() => {
    return country.cca2 === 'CA';
  }, [country]);

  const selectProvince = (p: string) => {
    const province = PROVINCES.find((pro) => pro.name === p);
    if (province?.name) {
      setProvince(province.name);
    }
  };

  return (
    <Layout.Column>
      <OpenSans.Primary weight="bold-700" size={24}>
        {getTranslation('What country are you in?')}
      </OpenSans.Primary>
      <Spacer.Vertical size={8} />
      <OpenSans.Secondary size={'s-16'}>
        {getTranslation('This information is used to help us maximize your tax savings.')}
      </OpenSans.Secondary>

      <Spacer.Vertical size={36} />
      <Layout.PressableRow
        align
        onClick={() => setShowCountryPicker(true)}
        px
        py={8}
        border={[2, 'solid', 'primary']}
        radius
        grow
        bg="navHeaderBackground"
        style={SHADOW_STYLE}
      >
        <CountryPicker
          onSelect={setCountry}
          onClose={() => setShowCountryPicker(false)}
          countryCode={country.cca2}
          visible={showCountryPicker}
        />
        <OpenSans.Primary>{country.name}</OpenSans.Primary>
        <Spacer.Flex />
        <Icon name="chevron-down" size={28} iconColor="brandPrimary" />
      </Layout.PressableRow>
      {isCanada ? (
        <ProvinceSelector
          onSelect={(p: string[]) => {
            if (p[0]) {
              selectProvince(p[0]);
            }
          }}
          selectedOptions={province ? [province] : []}
        />
      ) : null}
    </Layout.Column>
  );
};
